import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { getFile } from '@opu/utils';
import InternationalPaymentBanner from '../../../components/InternationalPaymentBanner/InternationalPaymentBanner';

const FIXED_EURO_PRICE = 35;

const BookPurchase = () => {
  const { t } = useTranslation();
  const tr = (key, fallback, options = {}) => t(key, { defaultValue: fallback, ...options });
  const [books, setBooks] = useState([]);
  const [cartDZD, setCartDZD] = useState([]);
  const [cartEUR, setCartEUR] = useState([]);
  const [currencyMode, setCurrencyMode] = useState('DZD'); // 'DZD' | 'EUR'
  const [visitorInfo, setVisitorInfo] = useState({
    name: '',
    email: '',
    emailConfirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [booksPerPage] = useState(20);
  const [isSearching, setIsSearching] = useState(false);
  const [referenceCode, setReferenceCode] = useState('');

 console.log('🔍 BookPurchase component loaded');
 console.log('🔍 Current URL:', window.location.href);

  useEffect(() => {
    fetchBooksWithPrices();
    
    // Check if a specific book is pre-selected from URL
    const urlParams = new URLSearchParams(window.location.search);
    const preSelectedBookId = urlParams.get('bookId');
    if (preSelectedBookId) {
      // Pre-select the book in the cart
      setTimeout(() => {
        const preSelectedBook = books.find(book => book.id === preSelectedBookId);
        if (preSelectedBook) {
          addToCart(preSelectedBook);
        }
      }, 1000); // Wait for books to load
    }
  }, [currentPage, searchTerm]);

  // Function to fetch a specific book by ID
  const fetchSpecificBook = async (bookId) => {
    try {
      console.log('🔍 Fetching specific book:', bookId);
      const response = await AxiosInstance.get(`/visitor/books/${bookId}`);
      console.log('📚 API Response:', response);
      console.log('📚 Response data:', response.data);
      console.log('📚 Response keys:', Object.keys(response));
      
      const bookData = response.data || response;
      console.log('📚 Book data to add:', bookData);
      console.log('📚 Book data type:', typeof bookData);
      
      if (bookData && !cartDZD.find(item => item.id === bookId)) {
        console.log('🛒 Adding specific book to cart:', bookData.title);
        addToCart(bookData);
      } else if (bookData && cartDZD.find(item => item.id === bookId)) {
        console.log('🛒 Specific book already in cart');
      } else {
        console.log('❌ No data in response or book already in cart');
      }
    } catch (error) {
      console.error('❌ Error fetching specific book:', error);
    }
  };

  // Separate useEffect for pre-selecting books from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const preSelectedBookId = urlParams.get('bookId');
    console.log('🔍 URL bookId parameter:', preSelectedBookId);
    console.log('🔍 Available books:', books.length);
    console.log('🔍 Current cart:', cartDZD.length);
    
    if (preSelectedBookId && books.length > 0) {
      const preSelectedBook = books.find(book => book.id === preSelectedBookId);
      console.log('🔍 Found pre-selected book:', preSelectedBook);
      
      if (preSelectedBook && !cartDZD.find(item => item.id === preSelectedBookId)) {
        console.log('🛒 Adding book to cart:', preSelectedBook.title);
        addToCart(preSelectedBook);
      } else if (preSelectedBook && cartDZD.find(item => item.id === preSelectedBookId)) {
        console.log('🛒 Book already in cart');
      } else {
        console.log('❌ Book not found in available books, fetching specific book...');
        // If book not found in current page, fetch it specifically
        fetchSpecificBook(preSelectedBookId);
      }
    }
  }, [books]);

  const fetchBooksWithPrices = async () => {
    setIsSearching(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: booksPerPage.toString()
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      // Fetch books with pagination and search
      const url = `/visitor/books?${params.toString()}`;
      console.log('🔍 Fetching books from:', url);
      
      const response = await AxiosInstance.get(url);
      
      setBooks(response.books || []);
      setTotalPages(response.totalPages || 1);
      setTotalBooks(response.total || 0);
      
      console.log(`📚 Loaded ${response.books?.length || 0} books (${response.total || 0} total)`);
      console.log(`🔍 Pagination info: Page ${response.page}, Total pages: ${response.totalPages}`);
      console.log('📚 Book IDs loaded:', response.books?.map(book => book.id));
      
    } catch (error) {
      console.error('❌ Error fetching books:', error);
      setError(t('bookPurchase.orderCreationError'));
      setBooks([]);
      setTotalPages(1);
      setTotalBooks(0);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchBooksWithPrices();
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // fetchBooksWithPrices will be called by useEffect
  };

  const generateReferenceCode = () => {
    const code = `INT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    setReferenceCode(code);
    return code;
  };

  useEffect(() => {
    if (currencyMode === 'EUR' && cartEUR.length > 0 && !referenceCode) {
      generateReferenceCode();
    }

    if (currencyMode === 'DZD' || cartEUR.length === 0) {
      setReferenceCode('');
    }
  }, [currencyMode, cartEUR.length, referenceCode]);

  const addToCart = (book) => {
    const cart = currencyMode === 'EUR' ? cartEUR : cartDZD;
    const setCart = currencyMode === 'EUR' ? setCartEUR : setCartDZD;

    console.log('🛒 addToCart called with book:', book);
    console.log('🛒 Current cart before adding:', cart);
    
    // For digital books, only allow one copy per book
    const existingItem = cart.find(item => item.id === book.id);
    if (!existingItem) {
      const newCart = [
        ...cart,
        {
          ...book,
          price: currencyMode === 'EUR' ? FIXED_EURO_PRICE : book.price,
          quantity: 1,
        },
      ];
      console.log('🛒 New cart after adding:', newCart);
      setCart(newCart);
      if (currencyMode === 'EUR' && referenceCode === '') {
        generateReferenceCode();
      }
      console.log('✅ Book added to cart successfully');
    } else {
      console.log('⚠️ Book already in cart');
    }
  };

  const removeFromCart = (bookId) => {
    if (currencyMode === 'EUR') {
      setCartEUR(cartEUR.filter(item => item.id !== bookId));
    } else {
      setCartDZD(cartDZD.filter(item => item.id !== bookId));
    }
  };

  // Removed updateQuantity since digital books don't need quantity controls

  const getTotalAmountDZD = () => {
    return cartDZD.reduce((total, item) => total + ((item.price || 0) * item.quantity), 0);
  };

  const getTotalAmountEUR = () => {
    return cartEUR.reduce((total, item) => total + (FIXED_EURO_PRICE * item.quantity), 0);
  };

  const formatPriceDZD = (amount) => `${Number(amount || 0).toLocaleString()} DA`;
  const formatPriceEuro = () => `€${FIXED_EURO_PRICE.toFixed(2)}`;

  const activeCart = useMemo(() => (currencyMode === 'EUR' ? cartEUR : cartDZD), [currencyMode, cartEUR, cartDZD]);

  const euroOrderDetails = useMemo(() => {
    if (cartEUR.length === 0) return '';
    const code = referenceCode || 'INT-XXXX';
    const lines = cartEUR.map(
      (item, idx) => `${idx + 1}. ${item.title} (ID: ${item.id}) - ${formatPriceEuro()}`
    );
    const total = getTotalAmountEUR();
    return [`Référence: ${code}`, `Total: €${total.toFixed(2)}`, ...lines].join('\n');
  }, [cartEUR, referenceCode]);

  const handlePurchase = async () => {
    if (currencyMode !== 'DZD') {
      setError('Le paiement en ligne est uniquement disponible en DZD. Utilisez le panier EUR pour générer la liste et payer par virement.');
      return;
    }

    if (cartDZD.length === 0) {
      setError(t('bookPurchase.pleaseSelectBook'));
      return;
    }

    if (!visitorInfo.name || !visitorInfo.email || !visitorInfo.emailConfirm) {
      setError(t('bookPurchase.pleaseFillFields'));
      return;
    }

    if (visitorInfo.email !== visitorInfo.emailConfirm) {
      setError('Les adresses email ne correspondent pas.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Créer la commande
      const orderData = {
        visitorName: visitorInfo.name,
        visitorEmail: visitorInfo.email,
        items: cartDZD.map(item => ({
          bookId: item.id,
          quantity: item.quantity
        }))
      };

      console.log('🔄 Creating order with data:', orderData);
      const orderResponse = await AxiosInstance.post('/visitor/orders', orderData);
      
      //  CRITICAL: Validate order creation before proceeding
      if (!orderResponse || !orderResponse.id) {
        throw new Error(t('bookPurchase.orderNotCreated'));
      }

      console.log('✅ Order created successfully:', orderResponse.id);
      
      // Verify order exists in database before redirecting
      try {
        const orderVerification = await AxiosInstance.get(`/visitor/orders/${orderResponse.id}`);
        if (!orderVerification || orderVerification.id !== orderResponse.id) {
          throw new Error(t('bookPurchase.orderNotFound'));
        }
        console.log('✅ Order verified in database');
      } catch (verifyError) {
        console.error('❌ Order verification failed:', verifyError);
        throw new Error(t('bookPurchase.cannotVerifyOrder'));
      }
      
      // Only redirect to payment after successful order creation and verification
      console.log('🔄 Redirecting to payment gateway...');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/visitor/payment/process';
      form.style.display = 'none';
      
      const orderIdInput = document.createElement('input');
      orderIdInput.type = 'hidden';
      orderIdInput.name = 'orderId';
      orderIdInput.value = orderResponse.id;
      form.appendChild(orderIdInput);
      
      const returnUrlInput = document.createElement('input');
      returnUrlInput.type = 'hidden';
      returnUrlInput.name = 'returnUrl';
      returnUrlInput.value = `${window.location.origin}/api/visitor/payment/callback`;
      form.appendChild(returnUrlInput);
      
      document.body.appendChild(form);
      form.submit();

    } catch (error) {
      console.error('❌ Purchase error:', error);
      console.error('❌ Error details:', error.response || error.message);
      
      // More specific error messages
      let errorMessage = t('bookPurchase.orderCreationError');
      if (error.response?.status === 400) {
        errorMessage = t('bookPurchase.invalidOrderData');
      } else if (error.response?.status === 500) {
        errorMessage = t('bookPurchase.serverError');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          {/* Hero Section */}
          <div className="text-center mb-5" style={{ padding: '3rem 0', backgroundColor: '#f8f9fa', borderRadius: '10px' }}>
            <h1 className="display-5 fw-bold mb-3" style={{ color: '#2c5530' }}>
              <i className="fas fa-book me-2"></i>
              {tr('bookPurchase.heroTitle', 'Achat de Livres Numériques')}
            </h1>
            <p className="lead" style={{ color: '#6c757d', fontSize: '1.2rem' }}>
              {tr('bookPurchase.heroSubtitle', 'Découvrez notre collection de livres numériques et procédez à l\'achat en quelques clics')}
            </p>
          </div>

          {/* Payment Mode Toggle */}
          <div className="card mb-4">
            <div className="card-body d-flex flex-wrap align-items-center gap-2">
              <strong className="me-2">Mode de paiement :</strong>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${currencyMode === 'DZD' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setCurrencyMode('DZD')}
                >
                  Algérie (DZD) - Paiement en ligne
                </button>
                <button
                  type="button"
                  className={`btn ${currencyMode === 'EUR' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setCurrencyMode('EUR')}
                >
                  International (EUR) - Virement bancaire
                </button>
              </div>
            </div>
          </div>

          {/* International Payment Banner */}
          <InternationalPaymentBanner
            mode={currencyMode}
            prefillOrderDetails={currencyMode === 'EUR' ? euroOrderDetails : ''}
            referenceCode={referenceCode}
          />

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSearch} className="row g-3">
                    <div className="col-md-8">
                      <div className="input-group" style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <span className="input-group-text" style={{ backgroundColor: '#ffffff', borderColor: '#dee2e6', borderRight: 'none' }}>
                          <i className="fas fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={tr('bookPurchase.searchPlaceholder', 'Rechercher par titre ou ISBN...')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          disabled={isSearching}
                          style={{ 
                            backgroundColor: '#ffffff !important', 
                            borderColor: '#dee2e6', 
                            borderLeft: 'none',
                            opacity: '1 !important',
                            boxShadow: 'none !important'
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <button
                        type="submit"
                        className="btn w-100"
                        disabled={isSearching}
                        style={{ 
                          backgroundColor: '#2c5530', 
                          borderColor: '#2c5530', 
                          color: 'white',
                          borderRadius: '8px'
                        }}
                      >
                        {isSearching ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {tr('bookPurchase.searching', 'Recherche...')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-search me-2"></i>
                            {tr('bookPurchase.search', 'Rechercher')}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                  
                  {totalBooks > 0 && (
                    <div className="mt-3 text-muted">
                      <small>
                        <i className="fas fa-info-circle me-1"></i>
                        {searchTerm
                          ? t('bookPurchase.booksFound', { count: totalBooks, term: searchTerm, defaultValue: `${totalBooks} livres trouvés pour "${searchTerm}"` })
                          : t('bookPurchase.totalBooksAvailable', { count: totalBooks, defaultValue: `${totalBooks} livres disponibles au total` })}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Liste des livres */}
            <div className="col-md-8">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>{tr('bookPurchase.availableBooks', 'Livres Disponibles')}</h4>
                {totalBooks > 0 && (
                  <small className="text-muted">
                    {tr('bookPurchase.page', 'Page')} {currentPage} {tr('bookPurchase.of', 'sur')} {totalPages}
                  </small>
                )}
              </div>
              
              {isSearching ? (
                <div className="text-center py-5">
                  <span className="spinner-border text-success" role="status" aria-hidden="true"></span>
                  <p className="mt-2 text-muted">{tr('bookPurchase.loadingBooks', 'Chargement des livres...')}</p>
                </div>
              ) : books.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-book fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">{tr('bookPurchase.noBooksFound', 'Aucun livre trouvé')}</h5>
                  <p className="text-muted">
                    {searchTerm
                      ? t('bookPurchase.noResultsFor', { term: searchTerm, defaultValue: `Aucun résultat pour "${searchTerm}"` })
                      : tr('bookPurchase.noBooksAvailable', 'Aucun livre disponible')}
                  </p>
                </div>
              ) : (
                <div className="row">
                {books.map((book) => (
                  <div className="col-md-6 col-lg-4 mb-4" key={book.id}>
                    <div className="card h-100 shadow-sm">
                      <div className="position-relative">
                        <img 
                          src={getFile('book', book.cover)}
                          className="card-img-top" 
                          alt={book.title}
                          style={{ height: '250px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/assets/images/book_default.png';
                          }}
                        />
                        <div className="position-absolute top-0 end-0 m-2">
                          <span className="badge bg-success">{tr('bookPurchase.digitalVersion', 'Version Numérique')}</span>
                        </div>
                      </div>
                      <div className="card-body d-flex flex-column">
                        <h5 className="card-title text-truncate" title={book.title}>{book.title}</h5>
                        <p className="card-text text-muted small">{book.description}</p>
                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <span className="h6 mb-0 d-block" style={{ color: '#2c5530' }}>{formatPriceDZD(book.price)}</span>
                          <small className="text-muted">{formatPriceEuro()} (prix en EUR)</small>
                        </div>
                            {activeCart.find(item => item.id === book.id) && (
                                <span className="badge bg-success">
                                  <i className="fas fa-check me-1"></i>{tr('bookPurchase.inCart', 'Dans le panier')}
                                </span>
                            )}
                          </div>
                          <button
                            className={`btn w-100 ${activeCart.find(item => item.id === book.id) ? 'btn-success' : ''}`}
                            onClick={() => addToCart(book)}
                            disabled={activeCart.find(item => item.id === book.id)}
                            style={{
                              backgroundColor: activeCart.find(item => item.id === book.id) ? '#28a745' : '#2c5530',
                              borderColor: activeCart.find(item => item.id === book.id) ? '#28a745' : '#2c5530',
                              color: 'white',
                              borderRadius: '8px'
                            }}
                          >
                            <i className={`fas ${activeCart.find(item => item.id === book.id) ? 'fa-check' : 'fa-shopping-cart'} me-2`}></i>
                            {activeCart.find(item => item.id === book.id) ? tr('bookPurchase.addedToCart', 'Ajouté') : tr('bookPurchase.addToCart', 'Ajouter au panier')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav aria-label="Book pagination">
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{
                            backgroundColor: currentPage === 1 ? '#f8f9fa' : '#2c5530',
                            borderColor: '#2c5530',
                            color: currentPage === 1 ? '#6c757d' : 'white'
                          }}
                        >
                          <i className="fas fa-chevron-left"></i> {t('bookPurchase.previous')}
                        </button>
                      </li>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNum)}
                              style={{
                                backgroundColor: currentPage === pageNum ? '#2c5530' : 'white',
                                borderColor: '#2c5530',
                                color: currentPage === pageNum ? 'white' : '#2c5530'
                              }}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          style={{
                            backgroundColor: currentPage === totalPages ? '#f8f9fa' : '#2c5530',
                            borderColor: '#2c5530',
                            color: currentPage === totalPages ? '#6c757d' : 'white'
                          }}
                        >
                          {t('bookPurchase.next')} <i className="fas fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>

            {/* Panier et formulaire */}
            <div className="col-md-4">
              <div className="card shadow">
                <div className="card-header text-white" style={{ backgroundColor: '#2c5530' }}>
                  <h5 className="mb-0">
                    <i className="fas fa-shopping-cart me-2"></i>
                    {currencyMode === 'EUR' ? 'Panier EUR (virement)' : tr('bookPurchase.cart', 'Panier')} ({activeCart.length} {t('bookPurchase.cartItems', { count: activeCart.length, defaultValue: 'articles' })})
                  </h5>
                </div>
                <div className="card-body">
                  {activeCart.length === 0 ? (
                    <p className="text-muted">{tr('bookPurchase.emptyCart', 'Panier vide')}</p>
                  ) : (
                    <>
                      {activeCart.map((item) => (
                        <div key={item.id} className="d-flex justify-content-between align-items-center mb-3 p-2 border rounded">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{item.title}</h6>
                            <small className="text-muted">{tr('bookPurchase.digitalBook', 'Livre numérique')}</small>
                            <br />
                            {currencyMode === 'EUR' ? (
                              <span className="text-primary fw-bold" style={{ color: '#2c5530' }}>{formatPriceEuro()}</span>
                            ) : (
                              <span className="text-primary fw-bold" style={{ color: '#2c5530' }}>{formatPriceDZD(item.price)}</span>
                            )}
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeFromCart(item.id)}
                            title={tr('bookPurchase.removeFromCart', 'Retirer du panier')}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                      
                      <hr />
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="fw-bold mb-0">{tr('bookPurchase.total', 'Total')}</h5>
                        <h4 className="fw-bold mb-0" style={{ color: '#2c5530' }}>
                          {currencyMode === 'EUR'
                            ? `${formatPriceEuro()} x ${activeCart.length} = €${(activeCart.length * FIXED_EURO_PRICE).toFixed(2)}`
                            : formatPriceDZD(getTotalAmountDZD())}
                        </h4>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Formulaire visiteur */}
              {currencyMode === 'DZD' && activeCart.length > 0 && (
                <div className="card mt-3 shadow">
                  <div className="card-header text-white" style={{ backgroundColor: '#28a745' }}>
                    <h5 className="mb-0">
                      <i className="fas fa-user me-2"></i>
                      {t('bookPurchase.contactInfo')}
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label htmlFor="visitorName" className="form-label">{tr('bookPurchase.fullName', 'Nom complet')}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="visitorName"
                        value={visitorInfo.name}
                        onChange={(e) => setVisitorInfo({ ...visitorInfo, name: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="visitorEmail" className="form-label">{tr('bookPurchase.email', 'Email')}</label>
                      <input
                        type="email"
                        className="form-control"
                        id="visitorEmail"
                        value={visitorInfo.email}
                        onChange={(e) => setVisitorInfo({ ...visitorInfo, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="visitorEmailConfirm" className="form-label">{tr('bookPurchase.confirmEmail', 'Confirmez votre email')}</label>
                      <input
                        type="email"
                        className="form-control"
                        id="visitorEmailConfirm"
                        value={visitorInfo.emailConfirm}
                        onChange={(e) => setVisitorInfo({ ...visitorInfo, emailConfirm: e.target.value })}
                        required
                      />
                      {visitorInfo.emailConfirm && visitorInfo.email !== visitorInfo.emailConfirm && (
                        <small className="text-danger">{tr('bookPurchase.emailMismatch', 'Les adresses email ne correspondent pas.')}</small>
                      )}
                    </div>
                    
                    <button
                      className="btn btn-lg w-100 py-3"
                      onClick={handlePurchase}
                      disabled={
                        loading ||
                        !visitorInfo.name ||
                        !visitorInfo.email ||
                        !visitorInfo.emailConfirm ||
                        visitorInfo.email !== visitorInfo.emailConfirm
                      }
                      style={{
                        backgroundColor: '#28a745',
                        borderColor: '#28a745',
                        color: 'white',
                        borderRadius: '8px'
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {tr('bookPurchase.processing', 'Traitement...')}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-credit-card me-2"></i>
                          {t('bookPurchase.proceedToPayment', { amount: formatPriceDZD(getTotalAmountDZD()), defaultValue: `Payer ${formatPriceDZD(getTotalAmountDZD())}` })}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Euro cart actions */}
              {currencyMode === 'EUR' && activeCart.length > 0 && (
                <div className="card mt-3 shadow">
                  <div className="card-header text-white" style={{ backgroundColor: '#2c5530' }}>
                    <h5 className="mb-0">
                      <i className="fas fa-euro-sign me-2"></i>
                      Virement bancaire (EUR)
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small">
                      {tr('bookPurchase.downloadEuroList', 'Téléchargez la liste de vos livres (avec ID et prix fixe) pour la joindre à votre reçu bancaire.')}
                    </p>
                    <button
                      className="btn w-100 mb-2"
                      onClick={() => {
                        const code = referenceCode || generateReferenceCode();
                        const total = getTotalAmountEUR();
                        const lines = [
                          `Référence: ${code}`,
                          `Total: €${total.toFixed(2)}`,
                          '',
                          ...activeCart.map(
                            (item, idx) => `${idx + 1}. ${item.title} (ID: ${item.id}) - €${FIXED_EURO_PRICE.toFixed(2)}`
                          ),
                        ];
                        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `commande-eur-${code}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      style={{
                        backgroundColor: '#2c5530',
                        borderColor: '#2c5530',
                        color: 'white',
                      }}
                    >
                      <i className="fas fa-download me-2"></i>
                      Télécharger la liste (EUR)
                    </button>
                    {referenceCode && (
                      <div className="alert alert-info py-2 mb-0">
                        <i className="fas fa-hashtag me-1"></i>
                        Référence à utiliser dans votre virement : <strong>{referenceCode}</strong>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookPurchase;
